import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ApiMethod } from 'src/app/decorators/api-method.decorator';
import { PreferredUserAccessInfo } from 'src/app/models/auth/preferred-user-access-info.model';
import { SiteNote } from 'src/app/models/site-notes/site-note.model';
import { UserAccountInfo } from 'src/app/models/user-account-info.model';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../auth.service';
import { AccountRouteService } from '../../interfaces/account-route-service.abstract';
import { CompleteSiteNoteRequest, SiteNoteRequest, UpdateSiteNoteDto } from 'src/app/models/site-notes/site-note-request.model';
import { CreateSiteNoteResponse } from 'src/app/models/site-notes/site-note-response.model';
import { SiteNoteAssignee } from 'src/app/models/site-notes/site-note-assignee.model';
import { SiteNoteResourceType } from 'src/app/models/site-notes/site-note-resource-type.model';

@Injectable({
	providedIn: 'root'
})
export class SiteNotesService extends AccountRouteService {

	private baseSiteNotesUrl = `${environment.baseUrl}/site-notes`;

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService
	) {
		super();
	}

	@ApiMethod()
	getSiteNotesList(token: string, filters: any = {}): Observable<SiteNote[]> {
		return this.httpClient.post<SiteNote[]>(`${this.baseSiteNotesUrl}/${token}/filter`, filters, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	getSiteNoteAssignees(token: string, jobId: number): Observable<SiteNoteAssignee[]> {
		return this.httpClient.get<any[]>(`${this.baseSiteNotesUrl}/${token}/${jobId}/users`);
	}

	@ApiMethod()
	getSiteNote(token: string, siteNoteId: number): Observable<SiteNote> {
		return this.httpClient.get<SiteNote>(`${this.baseSiteNotesUrl}/${token}/${siteNoteId}`);		
	}

	@ApiMethod()
	getReadonlySiteNote(token: string): Observable<SiteNote> {
		return this.httpClient.get<SiteNote>(`${this.baseSiteNotesUrl}/readonly/${token}`);
	}

	@ApiMethod()
	isUserAccountAllowedAccess(token: string): Observable<PreferredUserAccessInfo> {
		return this.httpClient.get<PreferredUserAccessInfo>(`${this.baseSiteNotesUrl}/${token}/is-authorized`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	addSiteNote(token: string, siteNote: SiteNoteRequest, photos): Observable<CreateSiteNoteResponse> {
		const formData = new FormData();
		formData.set('siteNote', JSON.stringify(siteNote));
		if (photos) {
			photos.forEach(file => formData.append('file[]', file, file.name));
		}
		return this.httpClient.post<CreateSiteNoteResponse>(`${this.baseSiteNotesUrl}/${token}`, formData);
	}

	@ApiMethod()
	completeSiteNote(token: string, siteNoteId: number, completeSiteNoteRequest: CompleteSiteNoteRequest): Observable<void> {
		return this.httpClient.put<void>(`${this.baseSiteNotesUrl}/${token}/complete/${siteNoteId}`, completeSiteNoteRequest);
	}

	@ApiMethod()
	updateSiteNote(token: string, siteNote: UpdateSiteNoteDto, photos): Observable<CreateSiteNoteResponse> {
		const formData = new FormData();
		formData.set('siteNote', JSON.stringify(siteNote));
		if (photos) {
			photos.forEach(file => formData.append('file[]', file, file.name));
		}

		return this.httpClient.put<CreateSiteNoteResponse>(`${this.baseSiteNotesUrl}/${token}`, formData);
	}

	@ApiMethod()
	public isSiteNoteAccessibleByUser(token: string, siteNoteId: number): Observable<boolean>{
		return this.httpClient.get<boolean>(`${this.baseSiteNotesUrl}/${token}/can-access/${siteNoteId}`).pipe(
			catchError((response: Response) => of(false)),
		);
	}

	@ApiMethod()
	public canCreateSiteNotes(token: string, jobId: number): Observable<boolean>{
		return this.httpClient.get<boolean>(`${this.baseSiteNotesUrl}/${token}/can-create/${jobId}`).pipe(
			catchError((response: Response) => of(false)),
		);
	}

	@ApiMethod()
	deleteSiteNote(token: string, siteNoteId: number): Observable<void> {
		return this.httpClient.delete<void>(`${this.baseSiteNotesUrl}/${token}/${siteNoteId}`);
	}

	@ApiMethod()
	getSiteNotesRedirectUriById(userAccountId: number): Observable<{ smartSyncUri: string }> {
		return this.httpClient.get<{ smartSyncUri: string }>(`${this.baseSiteNotesUrl}/${userAccountId}/id-url`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	getRedirectUri(token: string): Observable<{ smartSyncUri: string }> {
		return this.httpClient.get<{ smartSyncUri: string }>(`${this.baseSiteNotesUrl}/${token}/url`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	getUserAccountInfo(token: string, jobId: number | null = null): Observable<UserAccountInfo> {
		let query = jobId != null ? `?jobId=${jobId}`: '';
		return this.httpClient.get<UserAccountInfo>(`${this.baseSiteNotesUrl}/${token}/user-info${query}`);
	}

	@ApiMethod()
	getSiteNotesStatus(token: string): Observable<number> {
		return this.httpClient.get<number>(`${this.baseSiteNotesUrl}/${token}/ping`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	getSiteNoteAssetTypes(token: string, accountSiteId: number, isLocation: boolean, isAsset: boolean): Observable<SiteNoteResourceType[]> {
		var url = `${this.baseSiteNotesUrl}/${token}/types/${accountSiteId}?isLocation=${isLocation}&isAsset=${isAsset}`;
		return this.httpClient.get<SiteNoteResourceType[]>(url, { headers: this.authService.getAuthenticationHeader() })
	}

	@ApiMethod()
	getSiteNoteAssets(token: string, accountSiteId: number, resourceTypeId: number, isLocation: boolean, isAsset: boolean): Observable<any[]> {
		var url = `${this.baseSiteNotesUrl}/${token}/types/${accountSiteId}/assets/${resourceTypeId}?isLocation=${isLocation}&isAsset=${isAsset}`;
		return this.httpClient.get<any[]>(url, { headers: this.authService.getAuthenticationHeader() })
	}
}
